import { useCallback, useState } from 'react';
import type { ViewStyle } from 'react-native';

import { useAppTheme } from '@/styles/hooks/useAppTheme';

const RING_WIDTH = 2;

type Options = {
  /**
   * Color of the border shown when unfocused. Defaults to transparent (for
   * elements with no resting border). Pass the element's existing border color
   * (e.g. an outlined button's gray) so the ring recolors it instead of
   * stacking.
   */
  restingBorderColor?: string;
};

/**
 * Web-only keyboard focus ring for focusables (buttons etc.). Paper's
 * TouchableRipple deliberately does not render a focused state, so keyboard
 * users get no focus indication without this.
 *
 * RNW's Pressable tracks focus via real DOM focus/blur events and forwards
 * them as onFocus/onBlur (paper passes these through to TouchableRipple).
 * The ring shows whenever the element is focused; browsers only move focus
 * via keyboard Tab or programmatic focus, so mouse hover never shows it.
 *
 * The ring is a border (RNW compiles neither `outline*` nor a non-layout
 * alternative reliably). To avoid shifting layout when it appears, the border
 * width is constant and only its color flips on focus (resting/transparent →
 * primary). Apply `ringStyle` unconditionally so the space is reserved at
 * rest.
 */
export const useFocusRing = (
  options?: Options
): {
  focusProps: { onFocus: () => void; onBlur: () => void };
  ringStyle: ViewStyle;
} => {
  const theme = useAppTheme();
  const [focused, setFocused] = useState(false);

  const onFocus = useCallback(() => {
    setFocused(true);
  }, []);
  const onBlur = useCallback(() => {
    setFocused(false);
  }, []);

  return {
    focusProps: { onFocus, onBlur },
    ringStyle: {
      borderWidth: RING_WIDTH,
      borderColor: focused
        ? theme.colors.primary
        : (options?.restingBorderColor ?? 'transparent')
    }
  };
};
